/**
 * Async key-value store with a localStorage-shaped API, backed by IndexedDB.
 *
 * localStorage itself is capped at ~5–10 MB; the catalog's 100 MB budget does
 * not fit, so this wrapper is the seam ADR 0010 names. Callers still write
 * `getItem` / `setItem` / `removeItem` for the JSON index, and `getBlob` /
 * `setBlob` for the files beside it.
 */

export const BUDGET_BYTES = 100 * 1024 * 1024;
export const INDEX_KEY = 'catalog.json';

const DB_NAME = 'singline';
const DB_VERSION = 1;
const STORE = 'kv';

export type RecordKind = 'text' | 'blob';

export interface KvRecord {
  key: string;
  kind: RecordKind;
  /** Strings for the JSON index; ArrayBuffer for file bytes (Blobs do not
   *  round-trip through every IndexedDB implementation, including the test
   *  double). */
  value: string | ArrayBuffer;
  mime?: string;
  bytes: number;
}

export class QuotaExceeded extends Error {
  readonly neededBytes: number;
  readonly budgetBytes: number;
  readonly usedBytes: number;

  constructor(usedBytes: number, neededBytes: number, budgetBytes: number = BUDGET_BYTES) {
    super(
      `Library is full (${formatBytes(budgetBytes)}). ` +
        `This would need ${formatBytes(neededBytes)} more; ` +
        `${formatBytes(Math.max(0, budgetBytes - usedBytes))} free. ` +
        `Delete some scores to free space.`,
    );
    this.name = 'QuotaExceeded';
    this.usedBytes = usedBytes;
    this.neededBytes = neededBytes;
    this.budgetBytes = budgetBytes;
  }
}

export type CommitOp =
  | { type: 'setItem'; key: string; value: string }
  | { type: 'setBlob'; key: string; blob: Blob }
  | { type: 'remove'; key: string };

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
  });
}

function complete(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
  });
}

function byteLengthOf(value: string | ArrayBuffer): number {
  return typeof value === 'string' ? new Blob([value]).size : value.byteLength;
}

function recordToBlob(record: KvRecord): Blob {
  const buffer = record.value instanceof ArrayBuffer ? record.value : new TextEncoder().encode(String(record.value)).buffer;
  return new Blob([buffer], { type: record.mime ?? '' });
}

export function blobKey(fileName: string): string {
  return `blob:${fileName}`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export class LocalStore {
  private db: IDBDatabase | null = null;
  private readonly dbName: string;
  private readonly budgetBytes: number;

  constructor(options: { dbName?: string; budgetBytes?: number } = {}) {
    this.dbName = options.dbName ?? DB_NAME;
    this.budgetBytes = options.budgetBytes ?? BUDGET_BYTES;
  }

  async open(): Promise<IDBDatabase> {
    if (this.db) return this.db;
    const db = await new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(this.dbName, DB_VERSION);
      req.onupgradeneeded = () => {
        const next = req.result;
        if (!next.objectStoreNames.contains(STORE)) {
          next.createObjectStore(STORE, { keyPath: 'key' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error('Failed to open IndexedDB'));
    });
    db.onclose = () => {
      this.db = null;
    };
    this.db = db;
    return db;
  }

  async getItem(key: string): Promise<string | null> {
    const rec = await this.get(key);
    if (!rec || rec.kind !== 'text') return null;
    return rec.value as string;
  }

  async setItem(key: string, value: string): Promise<void> {
    await this.commit([{ type: 'setItem', key, value }]);
  }

  async removeItem(key: string): Promise<void> {
    await this.commit([{ type: 'remove', key }]);
  }

  async getBlob(key: string): Promise<Blob | null> {
    const rec = await this.get(key);
    if (!rec || rec.kind !== 'blob') return null;
    return recordToBlob(rec);
  }

  async setBlob(key: string, blob: Blob): Promise<void> {
    await this.commit([{ type: 'setBlob', key, blob }]);
  }

  async removeBlob(key: string): Promise<void> {
    await this.commit([{ type: 'remove', key }]);
  }

  async usageBytes(): Promise<number> {
    const all = await this.getAll();
    return all.reduce((sum, rec) => sum + rec.bytes, 0);
  }

  /**
   * Apply a set of writes atomically. If the resulting size would exceed the
   * 100 MB budget, nothing is stored and {@link QuotaExceeded} is thrown.
   */
  async commit(ops: CommitOp[]): Promise<void> {
    if (ops.length === 0) return;
    // ArrayBuffers, not Blobs: IndexedDB's structured clone of Blob is
    // patchy (happy-dom's test double returns a plain object). Read the
    // bytes before opening the transaction so an await cannot auto-commit it.
    const prepared: Array<
      | { type: 'setItem'; key: string; value: string }
      | { type: 'setBlob'; key: string; buffer: ArrayBuffer; mime: string }
      | { type: 'remove'; key: string }
    > = [];
    for (const op of ops) {
      if (op.type === 'setBlob') {
        prepared.push({
          type: 'setBlob',
          key: op.key,
          buffer: await op.blob.arrayBuffer(),
          mime: op.blob.type,
        });
      } else {
        prepared.push(op);
      }
    }

    const db = await this.open();
    const tx = db.transaction(STORE, 'readwrite');
    const done = complete(tx);
    const store = tx.objectStore(STORE);
    const existing = await request(store.getAll()) as KvRecord[];
    const map = new Map(existing.map((rec) => [rec.key, rec]));

    for (const op of prepared) {
      if (op.type === 'remove') {
        map.delete(op.key);
        continue;
      }
      if (op.type === 'setItem') {
        map.set(op.key, { key: op.key, kind: 'text', value: op.value, bytes: byteLengthOf(op.value) });
        continue;
      }
      map.set(op.key, {
        key: op.key,
        kind: 'blob',
        value: op.buffer,
        mime: op.mime,
        bytes: op.buffer.byteLength,
      });
    }

    const used = [...map.values()].reduce((sum, rec) => sum + rec.bytes, 0);
    if (used > this.budgetBytes) {
      const before = existing.reduce((sum, rec) => sum + rec.bytes, 0);
      // Leave the transaction empty so it commits as a no-op: nothing stored.
      await done;
      throw new QuotaExceeded(before, used - before, this.budgetBytes);
    }

    for (const op of prepared) {
      if (op.type === 'remove') {
        store.delete(op.key);
      } else {
        const rec = map.get(op.key);
        if (rec) store.put(rec);
      }
    }
    await done;
  }

  private async get(key: string): Promise<KvRecord | undefined> {
    const db = await this.open();
    const tx = db.transaction(STORE, 'readonly');
    const rec = await request(tx.objectStore(STORE).get(key)) as KvRecord | undefined;
    await complete(tx);
    return rec;
  }

  private async getAll(): Promise<KvRecord[]> {
    const db = await this.open();
    const tx = db.transaction(STORE, 'readonly');
    const all = await request(tx.objectStore(STORE).getAll()) as KvRecord[];
    await complete(tx);
    return all;
  }
}
